import { ref, computed, watch, markRaw } from 'vue'
import { createChatInstance } from './useChatAI'
import { fetchSessionDetail, fetchOpenTabs, setTabOpen, persistTabOrder } from './useChatSessions'

// ─────────────────────────────────────────────────────────────────────────────
// useChatTabs — multi-tab manager for Chat AI.
//
// Setiap tab membungkus satu `createChatInstance()` yang BENAR-BENAR independen
// (state + AbortController + fetch sendiri), jadi beberapa tab bisa streaming
// PARALEL: tab background tetap menulis ke state-nya sendiri sementara user
// membaca / bertanya di tab lain.
//
// State "tab mana yang terbuka" dipersist ke DB (kolom IsOpenTab/TabOrder pada
// AIReport.ChatSessions) lewat /api/chat/tabs, supaya setelah reload halaman
// tab muncul lagi. Tab yang belum pernah dikirimi pesan (belum punya sessionId)
// bersifat lokal saja — tidak ada yang dipersist sampai ia melahirkan sesi.
// ─────────────────────────────────────────────────────────────────────────────

let _tabUid = 0
function nextTabId() { _tabUid += 1; return `tab_${_tabUid}` }

// tabs: [{ id, instance(markRaw), loaded, stopSessionWatch }]
const tabs        = ref([])
const activeTabId = ref(null)
const currentDate = ref(null) // sessionDate yang sedang di-scope tab-tab ini

// Saat super-admin melihat history user lain (read-only), JANGAN persist state
// tab ke DB — sesi itu bukan milik user yang login. Toggle dari ChatAI.
let _persistEnabled = true
function setPersistEnabled(v) { _persistEnabled = !!v }
function pOpen(sid, isOpen, order)  { if (_persistEnabled && sid) setTabOpen(sid, isOpen, order) }
function pOrder(ids)                { if (_persistEnabled && ids.length) persistTabOrder(ids) }

function findTab(id) { return tabs.value.find(t => t.id === id) || null }

const activeTab = computed(() => findTab(activeTabId.value))
const activeInstance = computed(() => activeTab.value?.instance || null)

// View ringan untuk tab bar — reaktif terhadap perubahan ref di tiap instance.
const tabViews = computed(() => tabs.value.map(t => {
  const inst = t.instance
  const title = inst.sessionTitle.value
  const msgs  = inst.messages.value
  const firstUser = msgs.find(m => m.role === 'user' && m.content)
  const questionCount = msgs.filter(m => m.role === 'user' && m.content && !m.error).length
  return {
    id:         t.id,
    sessionId:  inst.sessionId.value,
    active:     t.id === activeTabId.value,
    streaming:  inst.isStreaming.value,
    hasError:   !!inst.error.value,
    questionCount,
    empty:      msgs.length === 0,
    // Judul tampil: title sesi → ringkasan pertanyaan pertama → "Chat baru".
    label: (title && title.trim())
      || (firstUser ? firstUser.content.replace(/\s+/g, ' ').trim() : '')
      || 'Chat baru',
  }
}))

const anyBackgroundStreaming = computed(() =>
  tabs.value.some(t => t.id !== activeTabId.value && t.instance.isStreaming.value)
)

// ── Internals ────────────────────────────────────────────────────────────────

// Saat sebuah instance mendapat sessionId (lahir setelah pesan pertama), persist
// sebagai tab terbuka + assign TabOrder = posisi sekarang.
function watchSessionBirth(tab) {
  return watch(tab.instance.sessionId, (sid, prev) => {
    if (sid && sid !== prev) {
      const order = tabs.value.findIndex(t => t.id === tab.id)
      pOpen(sid, true, order >= 0 ? order : null)
    }
  })
}

function makeTab() {
  const tab = {
    id: nextTabId(),
    instance: markRaw(createChatInstance()),
    loaded: true,
    stopSessionWatch: null,
  }
  tab.stopSessionWatch = watchSessionBirth(tab)
  return tab
}

// ── Public actions ───────────────────────────────────────────────────────────

// Pastikan minimal ada satu tab; jadikan aktif.
function ensureAtLeastOneTab() {
  if (!tabs.value.length) {
    const tab = makeTab()
    tabs.value.push(tab)
    activeTabId.value = tab.id
  } else if (!findTab(activeTabId.value)) {
    activeTabId.value = tabs.value[0].id
  }
}

function newTab() {
  const tab = makeTab()
  tabs.value.push(tab)
  activeTabId.value = tab.id
  return tab
}

async function activateTab(id) {
  const tab = findTab(id)
  if (!tab) return
  activeTabId.value = id
  // Lazy-load isi sesi tab yang direstore dari DB tapi belum dimuat.
  if (!tab.loaded && tab.instance.sessionId.value) {
    const detail = await fetchSessionDetail(tab.instance.sessionId.value)
    if (detail) tab.instance.loadSession(detail)
    tab.loaded = true
  }
}

async function closeTab(id) {
  const idx = tabs.value.findIndex(t => t.id === id)
  if (idx === -1) return
  const tab = tabs.value[idx]
  tab.instance.abortStream()
  if (tab.stopSessionWatch) tab.stopSessionWatch()
  const sid = tab.instance.sessionId.value
  tabs.value.splice(idx, 1)
  // Tab yang punya sesi → tandai tertutup di DB (sesi tetap di Riwayat).
  pOpen(sid, false)
  // Selalu sisakan satu tab. Kalau habis, buka tab kosong baru.
  if (!tabs.value.length) {
    newTab()
  } else if (activeTabId.value === id) {
    // Pindah fokus ke tetangga terdekat.
    activateTab(tabs.value[Math.min(idx, tabs.value.length - 1)].id)
  }
  persistOrder()
}

// Buka detail sesi (dari Riwayat) ke dalam sebuah tab. Kalau sesi sudah terbuka
// di salah satu tab, cukup aktifkan. Kalau tab aktif masih kosong, pakai itu;
// selain itu buat tab baru.
function openSessionInTab(detail) {
  if (!detail?.sessionId) return
  const existing = tabs.value.find(t => t.instance.sessionId.value === detail.sessionId)
  if (existing) { activateTab(existing.id); return }
  const active = activeTab.value
  const target = (active && active.instance.messages.value.length === 0 && !active.instance.sessionId.value)
    ? active
    : newTab()
  target.instance.loadSession(detail)
  target.loaded = true
  activeTabId.value = target.id
  const sid = target.instance.sessionId.value
  if (sid) {
    const order = tabs.value.findIndex(t => t.id === target.id)
    pOpen(sid, true, order >= 0 ? order : null)
  }
}

// Tutup tab yang memegang sessionId tertentu (mis. sesi-nya dihapus di Riwayat).
function closeTabBySessionId(sid) {
  if (!sid) return
  const tab = tabs.value.find(t => t.instance.sessionId.value === sid)
  if (tab) closeTab(tab.id)
}

function persistOrder() {
  const ids = tabs.value.map(t => t.instance.sessionId.value).filter(Boolean)
  pOrder(ids)
}

// Pindahkan tab ke index baru (drag-reorder) lalu persist urutannya.
function moveTab(fromId, toIndex) {
  const from = tabs.value.findIndex(t => t.id === fromId)
  if (from === -1) return
  const clamped = Math.max(0, Math.min(toIndex, tabs.value.length - 1))
  if (from === clamped) return
  const [moved] = tabs.value.splice(from, 1)
  tabs.value.splice(clamped, 0, moved)
  persistOrder()
}

// Tutup semua tab lokal (dipakai saat ganti tanggal) TANPA mengubah IsOpenTab di
// DB — pergantian tanggal = himpunan sesi yang berbeda, bukan menutup tab.
function disposeAllLocal() {
  for (const t of tabs.value) {
    t.instance.abortStream()
    if (t.stopSessionWatch) t.stopSessionWatch()
  }
  tabs.value = []
  activeTabId.value = null
}

// Restore tab terbuka untuk sebuah tanggal dari DB. Tab pertama dimuat penuh,
// sisanya lazy-load saat diaktifkan. Kalau tak ada tab tersimpan, buka 1 kosong.
async function restoreTabsForDate(sessionDate) {
  currentDate.value = sessionDate || null
  disposeAllLocal()
  const open = await fetchOpenTabs(sessionDate)
  if (!open.length) { ensureAtLeastOneTab(); return }
  for (const meta of open) {
    const tab = {
      id: nextTabId(),
      instance: markRaw(createChatInstance()),
      loaded: false,
      stopSessionWatch: null,
    }
    tab.instance.setSession(meta.sessionId, meta.title)
    tab.stopSessionWatch = watchSessionBirth(tab)
    tabs.value.push(tab)
  }
  // Aktifkan + muat penuh tab pertama.
  await activateTab(tabs.value[0].id)
}

export function useChatTabs() {
  return {
    tabs,
    activeTabId,
    activeTab,
    activeInstance,
    tabViews,
    anyBackgroundStreaming,
    ensureAtLeastOneTab,
    newTab,
    activateTab,
    closeTab,
    closeTabBySessionId,
    openSessionInTab,
    moveTab,
    disposeAllLocal,
    restoreTabsForDate,
    setPersistEnabled,
  }
}
